import { useEffect } from 'react';
import { Button, Flex, Text, Title, Icon } from '@nula/core-ui';
import { NulaLockup } from '@/components/nula-lockup/nula-lockup';
import styles from './auth-desktop.module.css';

/**
 * `/calendar/connected` — desktop calendar OAuth handoff page.
 *
 * The system browser lands here after Google or Microsoft finish the calendar
 * consent flow. Its only job is to fire `nula://calendar-connected`, handing
 * control back to the running Tauri app, and then show a Nula confirmation.
 *
 * Without this page the callback redirects straight to the `nula://` scheme.
 * That still activates the app, but the browser is left showing the provider's
 * consent screen — a tab the user has to work out is finished with. Sign-in
 * already solves this via `/auth/desktop`; this is its calendar sibling.
 *
 * The tab cannot auto-close: browsers refuse `window.close()` on tabs the user
 * navigated to. Telling the user, and offering a button that re-fires the deep
 * link if the OS prompt was dismissed, is the whole remedy available.
 */

export const CALENDAR_CONNECTED_DEEP_LINK = 'nula://calendar-connected';

/** Navigation seam — injected in tests, defaults to real window.location. */
export interface CalendarConnectedNav {
  go: (url: string) => void;
}

function defaultNav(): CalendarConnectedNav {
  return { go: (url) => window.location.replace(url) };
}

export function CalendarConnected({ nav }: { nav?: CalendarConnectedNav }) {
  const resolved = nav ?? defaultNav();

  useEffect(() => {
    resolved.go(CALENDAR_CONNECTED_DEEP_LINK);
    // Run once on mount — the deep link is fixed for the page's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex align="center" justify="center" className={styles.root}>
      <Flex vertical align="center" className={styles.card}>
        <NulaLockup height={40} className={styles.lockup} />
        <Flex align="center" justify="center" className={styles.seal}>
          <Icon name="check" size={22} weight={400} />
        </Flex>
        <Title level={1} className={styles.title}>
          Calendar connected
        </Title>
        <Text className={styles.message}>
          Nula is opening. Your events will appear once the first sync finishes.
        </Text>
        <Button
          variant="default"
          icon={<Icon name="open_in_new" size={16} weight={300} />}
          className={styles.action}
          onClick={() => resolved.go(CALENDAR_CONNECTED_DEEP_LINK)}
        >
          Return to Nula
        </Button>
        <Text className={styles.hint}>You can close this tab.</Text>
      </Flex>
    </Flex>
  );
}
